import { useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import {
  getNextFollowUp,
  getFollowUpHistory,
  getFollowUpStats,
  FollowUpDecision,
  FollowUpHistoryOptions,
  FollowUpStatsData,
  FollowUpStatsOptions,
  ActiveCadence,
} from '../../api/nextFollowUp';

// Re-export API types for convenience
export type { FollowUpDecision, FollowUpStatsData, ActiveCadence };

interface UseFollowUpDecisionsProps {
  contactId: string;
  enabled?: boolean;
}

// Hook for getting the next upcoming follow-up decision using the REST API
export const useNextFollowUpDecision = ({
  contactId,
  enabled = true,
}: UseFollowUpDecisionsProps) => {
  const { organization } = useOrganizationStore();
  const organizationId = organization?.id;

  return useQuery<FollowUpDecision | null, Error>({
    queryKey: ['nextFollowUpDecision', contactId, organizationId],
    queryFn: async () => {
      if (!contactId) {
        return null;
      }

      const response = await getNextFollowUp(contactId, organizationId);

      if (!response.success) {
        console.error('Error fetching next follow-up:', response.message);
        return null;
      }

      // Merge cadence from root level response into the decision data as activeCadence
      if (response.data && response.cadence) {
        return {
          ...response.data,
          activeCadence: response.cadence,
        };
      }

      return response.data;
    },
    enabled: enabled && !!contactId && !!organizationId,
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep unused data in cache for 10 minutes
    refetchInterval: 30000, // Refetch every 30 seconds for near real-time updates
    refetchOnMount: 'always', // Always refetch when component mounts
  });
};

// Hook for getting follow-up history using the REST API
export const useFollowUpHistory = ({
  contactId,
  enabled = true,
  limit = 50,
  includePast = false,
}: UseFollowUpDecisionsProps & { limit?: number; includePast?: boolean }) => {
  const { organization } = useOrganizationStore();
  const organizationId = organization?.id;

  return useQuery<FollowUpDecision[], Error>({
    queryKey: ['followUpHistory', contactId, organizationId, limit, includePast],
    queryFn: async () => {
      if (!contactId) {
        return [];
      }

      const options: FollowUpHistoryOptions = {
        organizationId,
        includePast,
        limit,
      };

      const response = await getFollowUpHistory(contactId, options);

      if (!response.success) {
        console.error('Error fetching follow-up history');
        return [];
      }

      return response.data;
    },
    enabled: enabled && !!contactId && !!organizationId,
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep unused data in cache for 10 minutes
    refetchOnMount: 'always', // Always refetch when component mounts
  });
};

interface UseFollowUpStatsProps {
  dateRange?: { from: Date | null; to: Date | null };
  allTime?: boolean;
  enabled?: boolean;
}

// Hook for getting follow-up statistics for the dashboard
// Endpoint: GET /org/:organization_id/followup-stats
export const useFollowUpStats = ({ dateRange, allTime, enabled = true }: UseFollowUpStatsProps) => {
  const { organization } = useOrganizationStore();
  const organizationId = organization?.id;

  return useQuery<FollowUpStatsData | null, Error>({
    queryKey: ['followUpStats', organizationId, allTime ? 'all-time' : dateRange?.from?.toISOString()],
    queryFn: async () => {
      if (!organizationId) {
        return null;
      }

      const options: FollowUpStatsOptions = allTime
        ? {}
        : { ...(dateRange?.from && { startDate: dateRange.from.toISOString() }) };

      const response = await getFollowUpStats(organizationId, options);

      if (!response.success) {
        console.error('Error fetching follow-up stats');
        return null;
      }

      return response.data;
    },
    enabled: enabled && !!organizationId,
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep unused data in cache for 10 minutes
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchInterval: enabled ? 1000 * 60 * 5 : false,
  });
};
